import { useMemo } from 'react'

import { ModalContext } from '@domain-group/suit-theme'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrDuplicateTrainingCourseModal from '@modals/CreateOrDuplicateTrainingCourseModal'

import PageContent from './components/PageContent'

type DefaultState = {
  showCreateOrDuplicateTrainingCourseModal: boolean,
}

type SetState = (state: Partial<DefaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrDuplicateTrainingCourseModal: {
      closeModal: () => setState({ showCreateOrDuplicateTrainingCourseModal: false }),
      createTrainingCourse: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      duplicateTrainingCourse: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState: DefaultState = {
  showCreateOrDuplicateTrainingCourseModal: false,
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrDuplicateTrainingCourseModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    pageFilters,
  } = useFilters()

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showCreateOrDuplicateTrainingCourseModal: (payload: object) => {
        setModalData('CreateOrDuplicateTrainingCourseModal', payload)
        setState({ showCreateOrDuplicateTrainingCourseModal: true })
      },
      ...filterCallbacks,
    },
    defaultFilters,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <CreateOrDuplicateTrainingCourseModal
          callbacks={callbacks('CreateOrDuplicateTrainingCourseModal', setState)}
          showModal={showCreateOrDuplicateTrainingCourseModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
